import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
  inject,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Subject, forkJoin, from, of } from 'rxjs';
import { map, switchMap, catchError, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

import { FormField, ActionButton, SectionButton, AddFormComponent } from '@shared/components/add-form/add-form.component';
import { LogicTransferFormConfig, LOGIC_TRANSFER_FORM_FIELDS } from '../../logic-transfer.config';
import { LogicTransferService } from '../../logic-transfer.service';
import { LogicTransferDetail } from '../../logic-transfer.config';
import { WarehouseControllerService, WarehouseQueryReqDTO, GoodsStockControllerService, StockQueryReqDTO, WarehouseQueryResVO, GoodsStockResVO, BASE_PATH } from '@app/api/stock-service';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { formatDateToISOWithoutTz } from '@shared/utils/date-formatter.util';
import { StockSelectionDialogComponent, StockSelectionItem } from '../stock-selection-dialog/stock-selection-dialog.component';
import { ErrorDialogService } from '@shared/services/error-dialog.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { PaginatedDropdownLoadParams, PaginatedDropdownConfig } from '@shared/utils/paginated-dropdown.util';
import {
  FefoOverrideDialogComponent,
  FefoOverrideDialogData,
  FefoOverrideDialogResult,
} from '../../../../shared/fefo-override-dialog/fefo-override-dialog.component';
import {
  fefoSuggestedBatchId,
  isLotRequiredFromResolve,
  needsFefoOverrideReason,
} from '../../../../shared/fefo-lot.util';

@Component({
  selector: 'app-logic-transfer-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ToastModule,
    AddFormComponent,
    StockSelectionDialogComponent,
    FefoOverrideDialogComponent,
  ],
  providers: [LogicTransferService],
  templateUrl: './logic-transfer-form.component.html',
  styleUrls: ['./logic-transfer-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicTransferFormComponent implements OnInit, OnDestroy, OnChanges {
  private fb = inject(FormBuilder);
  private warehouseController = inject(WarehouseControllerService);
  private goodsStockController = inject(GoodsStockControllerService);
  private errorDialogService = inject(ErrorDialogService);
  private logicTransferService = inject(LogicTransferService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly batchManagementService = inject(BatchManagementService);
  private http = inject(HttpClient);
  private basePath = inject(BASE_PATH);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  
  @Input() config!: LogicTransferFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @Input() set initialData(value: any) {
    this._initialData.set(value);
  }
  get initialData(): any {
    return this._initialData();
  }
  private _initialData = signal<any>(null);
  @Input() height: string = '100%';
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;
  
  // Form management
  form!: FormGroup;

  // Form data management
  formData = signal<any>({});
  loading = signal(false);
  private skipCanDeactivate = false;
  
  // Warehouse options - separate for source and receiving
  allWarehouses = signal<WarehouseQueryResVO[]>([]);
  sourceWarehouseOptions = signal<Array<{ label: string; value: string }>>([]);
  receivingWarehouseOptions = signal<Array<{ label: string; value: string }>>([]);
  
  // Separate signals to avoid reading formData in computed
  transferTypeSignal = signal<string>('');
  sourceWarehouseCodeSignal = signal<string>('');

  // Computed signal to check if transfer type is selected
  hasTransferType = computed(() => {
    return !!this.transferTypeSignal();
  });

  // Computed signal to check if source warehouse is selected
  hasSourceWarehouse = computed(() => {
    return !!this.sourceWarehouseCodeSignal();
  });

  // Updated form fields with warehouse options
  updatedFormFields = computed(() => {
    const fields = [...this.config.formFields];
    const sourceWarehouses = this.sourceWarehouseOptions();
    const receivingWarehouses = this.receivingWarehouseOptions();
    const hasTransferType = this.hasTransferType();
    const hasSourceWarehouse = this.hasSourceWarehouse();
    
    return fields.map(field => {
      // In edit mode: lock code (transfer order number), transfer type, and source warehouse
      if (this.editMode && field.controlName === 'code') {
        return {
          ...field,
          disabled: true,
        };
      }
      if (this.editMode && field.controlName === 'transferType') {
        return {
          ...field,
          disabled: true,
        };
      }
      if (this.editMode && field.controlName === 'supportWarehouseCode') {
        return {
          ...field,
          options: sourceWarehouses,
          disabled: true,
        };
      }
      if (field.controlName === 'supportWarehouseCode') {
        if (this.editMode) {
          return {
            ...field,
            options: sourceWarehouses,
            disabled: true,
          };
        }
        return {
          ...field,
          type: 'searchableDropdown' as const,
          options: hasTransferType ? sourceWarehouses : [],
          paginatedOptions: this.buildWarehousePaginatedOptions(() =>
            this.getSourceWarehouseTypeCodes(this.transferTypeSignal()),
          ),
          disabled: !hasTransferType || field.disabled,
        };
      }
      if (field.controlName === 'receiveWarehouseCode') {
        if (this.editMode) {
          return {
            ...field,
            type: 'searchableDropdown' as const,
            paginatedOptions: this.buildWarehousePaginatedOptions(
              () => ['STORE', 'BRANCH'],
            ),
            disabled: false,
            options: undefined,
          };
        }
        return {
          ...field,
          type: 'searchableDropdown' as const,
          options: hasTransferType ? receivingWarehouses : [],
          paginatedOptions: this.buildWarehousePaginatedOptions(() =>
            this.transferTypeSignal() ? ['STORE', 'BRANCH'] : null,
          ),
          disabled: !hasTransferType || field.disabled,
        };
      }
      return field;
    });
  });
  
  // Mapped warehouse codes for initial data
  mappedWarehouseCodes = signal<{ supportCode?: string; receiveCode?: string }>({});
  
  // Enriched initial data with mapped warehouse codes
  enrichedInitialData = computed(() => {
    const currentInitialData = this._initialData();
    if (!currentInitialData || !this.editMode) {
      return currentInitialData;
    }
    
    const mappedCodes = this.mappedWarehouseCodes();
    
    // Return enriched initialData with warehouse codes
    return {
      ...currentInitialData,
      supportWarehouseCode: mappedCodes.supportCode || currentInitialData.supportWarehouseCode,
      receiveWarehouseCode: mappedCodes.receiveCode || currentInitialData.receiveWarehouseCode,
    };
  });
  
  // Table data management
  productTableData = signal<LogicTransferDetail[]>([]);
  selectedProducts = signal<LogicTransferDetail[]>([]);
  
  // Dialog state - using StockSelectionDialogComponent
  showItemDialog = signal(false);
  dialogWarehouseCode = signal<string>('');

  fefoOverrideVisible = false;
  fefoOverrideData: FefoOverrideDialogData | null = null;
  private pendingFefoLines: LogicTransferDetail[] = [];
  private pendingSaveFormValue: any = null;
  
  // Get existing SKU codes to filter out already added items
  existingSkuCodes = computed(() => {
    return this.productTableData().map(detail => detail.skuCode).filter(code => !!code);
  });
  
  // Validation state
  validationErrors = signal<string[]>([]);
  fieldValidationErrors = signal<Record<string, string>>({});
  
  // Computed signal to check if form is valid
  isFormValid = computed(() => {
    const errors = this.validationErrors();
    const fieldErrors = this.fieldValidationErrors();
    return errors.length === 0 && Object.keys(fieldErrors).length === 0;
  });

  // Updated header actions with validation state
  updatedHeaderActions = computed(() => {
    const actions = [...(this.config.headerActions || [])];
    return actions.map(action => {
      if (action.action === 'save') {
        return {
          ...action,
          disabled: !this.isFormValid() || this.loading(),
        };
      }
      return action;
    });
  });

  // Computed property for updated table sections with current data
  updatedTableSections = computed(() => {
    if (!this.config.tableSections) return [];
    
    const hasSourceWarehouse = this.hasSourceWarehouse();
    // In edit/view mode, source warehouse always exists from initialData, so button should always be enabled
    const canAddProducts = (this.editMode || this.viewMode)
      ? !!this._initialData()?.supportWarehouseCode || !!this._initialData()?.supportLogicWarehouseId || hasSourceWarehouse
      : hasSourceWarehouse;
    
    return this.config.tableSections.map(section => ({
      ...section,
      data: this.productTableData(),
      selection: this.selectedProducts(),
      actions: section.actions?.map(action => {
        if (action.action === 'deleteSelected') {
          return {
            ...action,
            disabled: this.selectedProducts().length === 0,
          };
        }
        if (action.action === 'selectProducts') {
          return {
            ...action,
            disabled: !canAddProducts,
          };
        }
        return action;
      }),
    }));
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Reset flag when component initializes
    this.initialDataProcessed = false;
    this.initializeForm();
    this.setupFormValueChanges();
    // Load warehouses first, then handle initial data
    this.loadAllWarehouses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAllWarehouses(): void {
    this.logicTransferService.loadAllWarehouses().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
        next: (warehouses: WarehouseQueryResVO[]) => {
          this.allWarehouses.set(warehouses);

          if (!this.editMode && this.transferTypeSignal()) {
            this.updateSourceWarehouseOptionsByType(this.transferTypeSignal());
            this.updateReceivingWarehouseOptionsByType(this.transferTypeSignal());
            this.reloadWarehousePaginatedDropdowns();
          }

          if (this.initialData && warehouses.length > 0) {
            this.handleInitialDataAfterWarehousesLoaded();
          }
        },
        error: () => {
          this.allWarehouses.set([]);
          this.sourceWarehouseOptions.set([]);
        },
      });
  }

  private buildWarehousePaginatedOptions(
    getTypeCodes: () => string[] | null | undefined,
  ): PaginatedDropdownConfig {
    return {
      loadPage: (params: PaginatedDropdownLoadParams) => {
        const typeCodes = getTypeCodes();
        if (!typeCodes?.length) {
          return of({ options: [], pageContentLength: 0 });
        }
        return this.warehouseOptionsService.getOptionsPage(params, {
          valueKey: 'code',
          typeCodes,
        });
      },
      mapCurrentToOption: (value) =>
        this.mapWarehouseCodeToOption(String(value)),
    };
  }

  private mapWarehouseCodeToOption(code: string): {
    label: string;
    value: string;
  } {
    const fromService = this.warehouseOptionsService.getWarehouseByCode(code);
    if (fromService?.code) {
      return {
        label: fromService.name
          ? `${fromService.code} - ${fromService.name}`
          : fromService.code,
        value: fromService.code,
      };
    }

    const fromCache = this.allWarehouses().find((warehouse) => warehouse.code === code);
    if (fromCache?.code) {
      return {
        label: fromCache.name
          ? `${fromCache.code} - ${fromCache.name}`
          : fromCache.code,
        value: fromCache.code,
      };
    }

    return { label: code, value: code };
  }

  private getSourceWarehouseTypeCodes(
    transferType: string,
  ): string[] | undefined {
    if (transferType === 'MAIN_TO_STORE') {
      return ['MAIN'];
    }
    if (transferType === 'STORE_TO_STORE') {
      return ['STORE', 'BRANCH'];
    }
    return undefined;
  }

  /**
   * Handle initial data after warehouses are loaded (edit mode)
   */
  private initialDataProcessed = false; // Flag to prevent duplicate processing

  /**
   * Map initial data items to product table format
   * @param stockMap Optional map of skuCode -> stockNum from API
   * @returns Mapped product details array
   */
  private mapInitialDataItemsToProductDetails(stockMap?: Map<string, number>): LogicTransferDetail[] {
    if (!this.initialData?.items || this.initialData.items.length === 0) {
      return [];
    }

    return this.initialData.items.map((item: any) => {
      const stockNum = stockMap?.get(item.skuCode) ?? item.stockNum ?? 0;
      return {
        id: item.id || this.generateProductId(),
        skuCode: item.skuCode || '',
        skuName: item.skuName || '',
        spuCode: item.spuCode || '',
        spuName: item.spuName || '',
        quantity: item.plannedQty || 0,
        batchId: item.batchId || '',
        stockNum: stockNum,
      };
    });
  }

  private handleInitialDataAfterWarehousesLoaded(): void {
    if (!this.initialData || this.initialDataProcessed) {
      return;
    }

    const transferType = this.initialData.transferType || '';
    
    if (transferType) {
      // Set transfer type signal
      this.transferTypeSignal.set(transferType);
      
      // Update receiving warehouse options (STORE/BRANCH warehouses)
      this.updateReceivingWarehouseOptionsByType(transferType);
      
      // Map warehouses from initialData
      this.ensureInitialWarehousesMappedForEdit();
      
      // Get mapped warehouse codes (from mappedWarehouseCodes signal set by ensureInitialWarehousesMappedForEdit)
      const mappedCodes = this.mappedWarehouseCodes();
      const supportCode = mappedCodes.supportCode || this.form.get('supportWarehouseCode')?.value;
      const receiveCode = mappedCodes.receiveCode || this.form.get('receiveWarehouseCode')?.value;
      
      // Populate form fields from initialData (including mapped warehouse codes)
      this.form.patchValue({
        code: this.initialData.code || '',
        transferType: transferType,
        description: this.initialData.description || '',
        supportWarehouseCode: supportCode || '',
        receiveWarehouseCode: receiveCode || '',
        expectedReceiveDate: this.initialData.expectedReceiveDate 
          ? new Date(this.initialData.expectedReceiveDate) 
          : null,
      }, { emitEvent: false });
      
      // Ensure signals are updated after form is patched
      if (supportCode) {
        this.sourceWarehouseCodeSignal.set(supportCode);
      }
      
      // Load stock for items first, then populate table data with stock information
      // This ensures stockNum is correctly set before any validation runs
      const sourceWarehouseCode = this.form.get('supportWarehouseCode')?.value;
      if (sourceWarehouseCode && this.initialData.items && this.initialData.items.length > 0) {
        // Prepare SKU codes from items
        const skuCodes = this.initialData.items
          .map((item: any) => item.skuCode)
          .filter((code: string) => !!code);
        
        if (skuCodes.length > 0) {
          // Load stock first, then set productTableData with correct stockNum
          this.logicTransferService.loadStockForItems(sourceWarehouseCode, skuCodes).pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            next: (stockItems: GoodsStockResVO[]) => {
              // Create a map of skuCode -> availableQuantity (stockNum - reservedNum) for quick lookup
              const stockMap = new Map<string, number>();
              stockItems.forEach((stockItem: any) => {
                if (stockItem.skuCode) {
                  // availableQuantity is computed by IMS in LogicTransferService.loadStockForItems()
                  // - not re-derived here from raw stockNum/reservedNum.
                  stockMap.set(stockItem.skuCode, stockItem.availableQuantity ?? 0);
                }
              });

              // Map items with correct stockNum from API
              const mappedDetails = this.mapInitialDataItemsToProductDetails(stockMap);
              this.productTableData.set(mappedDetails);
              this.initialDataProcessed = true; // Mark as processed
            },
            error: (error) => {
              // If stock load fails, still populate table with items (fallback)
              const mappedDetails = this.mapInitialDataItemsToProductDetails();
              this.productTableData.set(mappedDetails);
              this.initialDataProcessed = true; // Mark as processed
            },
          });
        } else {
          // No SKU codes, populate table without stock
          const mappedDetails = this.mapInitialDataItemsToProductDetails();
          this.productTableData.set(mappedDetails);
          this.initialDataProcessed = true; // Mark as processed
        }
      } else {
        // No source warehouse or items, populate table without stock
        const mappedDetails = this.mapInitialDataItemsToProductDetails();
        if (mappedDetails.length > 0) {
          this.productTableData.set(mappedDetails);
        }
        this.initialDataProcessed = true; // Mark as processed
      }
    }
  }

  private updateSourceWarehouseOptions(): void {
    // Use transferTypeSignal instead of formData to avoid timing issues
    const transferType = this.transferTypeSignal();
    if (transferType) {
      this.updateSourceWarehouseOptionsByType(transferType);
    } else {
      // Also check form value as fallback
      const formValue = this.form.value;
      const formTransferType = formValue?.transferType;
      if (formTransferType) {
        this.transferTypeSignal.set(formTransferType);
        this.updateSourceWarehouseOptionsByType(formTransferType);
      } else {
        this.sourceWarehouseOptions.set([]);
      }
    }
  }

  private updateSourceWarehouseOptionsByType(transferType: string): void {
    const allWarehouses = this.allWarehouses();

    if (!transferType || allWarehouses.length === 0) {
      this.sourceWarehouseOptions.set([]);
      return;
    }

    let targetTypeCodes: string[];
    if (transferType === 'MAIN_TO_STORE') {
      targetTypeCodes = ['MAIN'];
    } else if (transferType === 'STORE_TO_STORE') {
      targetTypeCodes = ['STORE', 'BRANCH'];
    } else {
      this.sourceWarehouseOptions.set([]);
      return;
    }

    let filteredWarehouses = allWarehouses.filter((warehouse) => {
      const matchesType = targetTypeCodes.includes(warehouse.typeCode || '');
      const isActive = warehouse.status === true;
      return matchesType && isActive;
    });

    if (filteredWarehouses.length === 0) {
      filteredWarehouses = allWarehouses.filter(
        (warehouse) => warehouse.status === true
      );
    }

    const options = filteredWarehouses.map((warehouse) => ({
      label: warehouse.name
        ? `${warehouse.code} - ${warehouse.name}`
        : warehouse.code || '',
      value: warehouse.code || '',
    }));

    const currentOptions = this.sourceWarehouseOptions();
    const optionsChanged =
      currentOptions.length !== options.length ||
      currentOptions.some((opt, idx) => opt.value !== options[idx]?.value);

    if (optionsChanged) {
      this.sourceWarehouseOptions.set(options);
    }
  }


  private mapWarehouseIdToCode(id: string | number | null | undefined): string {
    if (id === null || id === undefined) return '';
    const idNum = typeof id === 'string' ? Number(id) : id;
    if (isNaN(idNum as number)) return '';

    const allWarehouses = this.allWarehouses();
    const found = allWarehouses.find((w) => {
      const wId = typeof w.id === 'string' ? Number(w.id) : w.id;
      return wId === idNum;
    });

    return found?.code || '';
  }

  private ensureInitialWarehousesMapped(): void {
    if (!this.initialData) return;

    const formValue = this.form?.value || {};

    // Map support warehouse
    let supportCode = formValue.supportWarehouseCode || this.initialData.supportWarehouseCode;
    if (!supportCode) {
      supportCode =
        this.mapWarehouseIdToCode(this.initialData.supportLogicWarehouseId) ||
        this.mapWarehouseIdToCode(this.initialData.supportWarehouseId);
    }

    // Map receiving warehouse
    let receiveCode = formValue.receiveWarehouseCode || this.initialData.receiveWarehouseCode;
    if (!receiveCode) {
      receiveCode =
        this.mapWarehouseIdToCode(this.initialData.receivingLogicWarehouseId) ||
        this.mapWarehouseIdToCode(this.initialData.receiveWarehouseId);
    }

    if (supportCode || receiveCode) {
      this.form.patchValue(
        {
          supportWarehouseCode: supportCode || formValue.supportWarehouseCode,
          receiveWarehouseCode: receiveCode || formValue.receiveWarehouseCode,
        },
        { emitEvent: false }
      );

      if (supportCode) {
        this.sourceWarehouseCodeSignal.set(supportCode);
      }
    }
  }

  /**
   * For edit mode: Map source warehouse directly from initialData (no filter by type)
   * Source warehouse: disabled, map from supportLogicWarehouseId
   * Receiving warehouse: enabled, map from receivingLogicWarehouseId, show options for STORE/BRANCH warehouses
   */
  private ensureInitialWarehousesMappedForEdit(): void {
    if (!this.initialData) {
      return;
    }

    const formValue = this.form?.value || {};

    // Map support warehouse from ID (priority: supportLogicWarehouseId > supportWarehouseId > supportWarehouseCode)
    let supportCode = formValue.supportWarehouseCode || this.initialData.supportWarehouseCode;
    if (!supportCode && this.initialData.supportLogicWarehouseId) {
      supportCode = this.mapWarehouseIdToCode(this.initialData.supportLogicWarehouseId);
    }
    if (!supportCode && this.initialData.supportWarehouseId) {
      supportCode = this.mapWarehouseIdToCode(this.initialData.supportWarehouseId);
    }

    // Map receiving warehouse from ID (priority: receivingLogicWarehouseId > receiveWarehouseId > receiveWarehouseCode)
    let receiveCode = formValue.receiveWarehouseCode || this.initialData.receiveWarehouseCode;
    if (!receiveCode && this.initialData.receivingLogicWarehouseId) {
      receiveCode = this.mapWarehouseIdToCode(this.initialData.receivingLogicWarehouseId);
    }
    if (!receiveCode && this.initialData.receiveWarehouseId) {
      receiveCode = this.mapWarehouseIdToCode(this.initialData.receiveWarehouseId);
    }

    // Set source warehouse options to include only the mapped warehouse (disabled in edit mode)
    if (supportCode) {
      const allWarehouses = this.allWarehouses();
      const sourceWarehouse = allWarehouses.find((w) => w.code === supportCode);
      if (sourceWarehouse) {
        const sourceOption = {
          label: sourceWarehouse.name
            ? `${sourceWarehouse.code} - ${sourceWarehouse.name}`
            : sourceWarehouse.code || '',
          value: sourceWarehouse.code || '',
        };
        this.sourceWarehouseOptions.set([sourceOption]);
      }
    }

    // Receiving warehouse options are already set by updateReceivingWarehouseOptionsByType
    // We just need to ensure the form value is set

    if (supportCode || receiveCode) {
      // Store mapped codes in signal for enrichedInitialData
      this.mappedWarehouseCodes.set({
        supportCode: supportCode || undefined,
        receiveCode: receiveCode || undefined,
      });
      
      this.form.patchValue(
        {
          supportWarehouseCode: supportCode || formValue.supportWarehouseCode,
          receiveWarehouseCode: receiveCode || formValue.receiveWarehouseCode,
        },
        { emitEvent: false }
      );

      if (supportCode) {
        this.sourceWarehouseCodeSignal.set(supportCode);
      }
      
      // Force change detection to ensure UI updates
      this.cdr.markForCheck();
    }
  }

  private setupFormValueChanges(): void {
    // Watch for transfer type changes to update source warehouse options
    this.form
      .get('transferType')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((transferType: string) => {
        // In edit mode, skip this logic (warehouse options are already set and transferType is disabled)
        if (this.editMode) {
          return;
        }
        
        // Update signal immediately
        this.transferTypeSignal.set(transferType || '');
        
        // Clear warehouses first to avoid showing wrong options
        this.form.patchValue({ 
          supportWarehouseCode: '',
          receiveWarehouseCode: '' 
        }, { emitEvent: false });
        this.sourceWarehouseCodeSignal.set('');
        
        if (!transferType) {
          this.sourceWarehouseOptions.set([]);
          this.receivingWarehouseOptions.set([]);
        } else {
          this.updateSourceWarehouseOptionsByType(transferType);
          this.updateReceivingWarehouseOptionsByType(transferType);
          this.reloadWarehousePaginatedDropdowns();
        }
      });

    // Note: Source warehouse changes are handled in onFormValueChanges()
    // because the form is managed by add-form component
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      code: [''], // User inputs manually, no auto generation
      transferType: [''],
      description: [''],
      supportWarehouseCode: [''],
      receiveWarehouseCode: [''],
      expectedReceiveDate: [null],
    });
  }

  private handleInitialData(): void {
    // This method is kept for compatibility but logic is moved to handleInitialDataAfterWarehousesLoaded
    // Warehouse mapping should only happen after warehouses are loaded
    if (this.initialData && this.initialData.transferType) {
      this.transferTypeSignal.set(this.initialData.transferType);
    }
  }

  /**
   * Load stock data for items in edit mode
   * Calls service method with list of SKU codes and source warehouse code
   */
  private loadStockForItems(warehouseCode: string): void {
    const items = this.productTableData();
    if (!items || items.length === 0) {
      return;
    }

    const skuCodes = items.map(item => item.skuCode).filter(code => !!code);
    if (skuCodes.length === 0) {
      return;
    }

    this.logicTransferService.loadStockForItems(warehouseCode, skuCodes).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (stockItems: GoodsStockResVO[]) => {
        // Create a map of skuCode -> availableQuantity (stockNum - reservedNum) for quick lookup
        const stockMap = new Map<string, number>();
        stockItems.forEach((stockItem: any) => {
          if (stockItem.skuCode) {
            // availableQuantity is computed by IMS in LogicTransferService.loadStockForItems()
            // - not re-derived here from raw stockNum/reservedNum.
            stockMap.set(stockItem.skuCode, stockItem.availableQuantity ?? 0);
          }
        });

        // Update productTableData with available quantities (stockNum - reservedNum)
        const updatedItems = items.map(item => {
          const availableQuantity = stockMap.get(item.skuCode) ?? item.stockNum;
          return {
            ...item,
            stockNum: availableQuantity,
          };
        });

        this.productTableData.set(updatedItems);
      },
      error: (error) => {
        // Error is already handled in service
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && changes['initialData'].currentValue) {
      // If warehouses are already loaded, handle initial data
      if (this.editMode && this.allWarehouses().length > 0 && this.initialData) {
        this.handleInitialDataAfterWarehousesLoaded();
      }
      // If warehouses not loaded yet, handleInitialDataAfterWarehousesLoaded will be called
      // when warehouses are loaded in loadAllWarehouses subscribe
    }
  }

  onFormValueChanges(value: any): void {
    // Sync internal FormGroup so save uses latest values
    if (this.form) {
      this.form.patchValue(value, { emitEvent: false });
    }

    const previousTransferType = this.transferTypeSignal();
    const previousSourceWarehouse = this.sourceWarehouseCodeSignal();
    const newTransferType = value.transferType || '';
    const newSourceWarehouse = value.supportWarehouseCode || '';
    
    this.formData.set(value);
    // Update separate signals to avoid reading formData in computed
    this.transferTypeSignal.set(newTransferType);
    this.sourceWarehouseCodeSignal.set(newSourceWarehouse);
    
    // In edit mode, don't update warehouse options from form changes
    // They are already set in handleInitialDataAfterWarehousesLoaded
    if (this.editMode) {
      this.validateBeforeSave();
      return;
    }
    
    if (newTransferType !== previousTransferType) {
      if (!newTransferType) {
        this.sourceWarehouseOptions.set([]);
        this.receivingWarehouseOptions.set([]);
        this.reloadWarehousePaginatedDropdowns();
      } else {
        this.updateSourceWarehouseOptionsByType(newTransferType);
        this.updateReceivingWarehouseOptionsByType(newTransferType);
        this.reloadWarehousePaginatedDropdowns();
      }

      if (previousTransferType) {
        this.addFormComponent?.form?.patchValue(
          {
            supportWarehouseCode: '',
            receiveWarehouseCode: '',
          },
          { emitEvent: false },
        );
        this.sourceWarehouseCodeSignal.set('');
      }
    }

    // Re-validate form to update save button state
    this.validateBeforeSave();
  }

  private reloadWarehousePaginatedDropdowns(): void {
    setTimeout(() => {
      this.addFormComponent?.reloadPaginatedField('supportWarehouseCode');
      this.addFormComponent?.reloadPaginatedField('receiveWarehouseCode');
      this.cdr.markForCheck();
    }, 0);
  }

  private updateReceivingWarehouseOptionsByType(transferType: string): void {
    const allWarehouses = this.allWarehouses();

    if (!transferType || allWarehouses.length === 0) {
      this.receivingWarehouseOptions.set([]);
      return;
    }

    let filteredWarehouses = allWarehouses.filter(
      (warehouse) =>
        (warehouse.typeCode === 'STORE' || warehouse.typeCode === 'BRANCH') &&
        warehouse.status === true
    );

    if (filteredWarehouses.length === 0) {
      filteredWarehouses = allWarehouses.filter(
        (warehouse) => warehouse.status === true
      );
    }

    const options = filteredWarehouses.map((warehouse) => ({
      label: warehouse.name
        ? `${warehouse.code} - ${warehouse.name}`
        : warehouse.code || '',
      value: warehouse.code || '',
    }));

    this.receivingWarehouseOptions.set(options);
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        this.saveLogicTransfer(event.data);
        break;
      case 'cancel':
        this.cancel();
        break;
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    switch (event.action) {
      case 'selectProducts':
        this.openProductSelectionDialog();
        break;
      case 'deleteSelected':
        this.onDeleteSelectedProducts();
        break;
      case 'deleteDetail':
        if (event.data) {
          this.onDeleteProduct(event.data);
        }
        break;
      case 'selectionChange':
        this.onSelectionChange(event.section, event.data);
        break;
      case 'cellValueChange':
        if (event.data) {
          this.onCellValueChange(event.data);
        }
        break;
    }
  }

  onSelectionChange(section: string, selectedItems: any[]): void {
    // Always update selection - there's only one table section (product details)
    this.selectedProducts.set(selectedItems || []);
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    const currentData = this.productTableData();
    const index = currentData.findIndex(item => item.skuCode === event.rowData.skuCode);
    
    if (index !== -1) {
      const updatedProduct: LogicTransferDetail = {
        ...currentData[index],
        [event.prop]: event.value,
      };
      if (event.prop === 'batchId' || event.prop === 'quantity') {
        updatedProduct.overrideReason = undefined;
        updatedProduct.fefoSuggestedBatchId = undefined;
      }
      currentData[index] = updatedProduct;
      this.productTableData.set([...currentData]);
      
      // Validate quantity if prop is 'quantity'
      if (event.prop === 'quantity') {
        const fieldErrors: Record<string, string> = { ...this.fieldValidationErrors() };
        
        // Clear previous error for this field
        delete fieldErrors[`${index}_quantity`];
        
        // Validate quantity
        if (!updatedProduct.quantity || updatedProduct.quantity <= 0) {
          fieldErrors[`${index}_quantity`] = this.translateService.instant('logic_transfer.form.validation.quantity_required');
        } else if (updatedProduct.stockNum !== undefined && updatedProduct.stockNum !== null && updatedProduct.quantity > updatedProduct.stockNum) {
          fieldErrors[`${index}_quantity`] = this.translateService.instant('logic_transfer.form.validation.quantity_exceeds_stock');
        }
        
        this.fieldValidationErrors.set(fieldErrors);
      } else {
        // Clear validation error for other fields
        this.clearValidationError('products');
      }
      
      // Re-validate form to update save button state
      this.validateBeforeSave();
    }
  }

  onFefoOverrideResult(result: FefoOverrideDialogResult): void {
    const current = this.pendingFefoLines[0];
    if (!result.confirmed || !result.reason?.trim()) {
      this.pendingFefoLines = [];
      this.pendingSaveFormValue = null;
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'outbound_management.messages.fefo_override_required',
        ),
      });
      this.cdr.markForCheck();
      return;
    }
    if (current) {
      const products = this.productTableData().map((product) =>
        product.skuCode === current.skuCode
          ? { ...product, overrideReason: result.reason!.trim() }
          : product,
      );
      this.productTableData.set(products);
    }
    this.pendingFefoLines = this.pendingFefoLines.slice(1);
    if (this.pendingFefoLines.length > 0) {
      this.openNextFefoOverrideDialog();
      return;
    }
    const formValue = this.pendingSaveFormValue;
    this.pendingSaveFormValue = null;
    if (formValue) {
      this.loading.set(true);
      this.submitLogicTransfer(formValue);
    }
  }

  private openNextFefoOverrideDialog(): void {
    const line = this.pendingFefoLines[0];
    if (!line) {
      return;
    }
    this.fefoOverrideData = {
      skuCode: line.skuCode,
      chosenLot: line.batchId,
      suggestedLot: line.fefoSuggestedBatchId,
    };
    this.fefoOverrideVisible = true;
    this.cdr.markForCheck();
  }

  getFieldError(rowIndex: number, fieldName: string): string | null {
    return this.fieldValidationErrors()[`${rowIndex}_${fieldName}`] || null;
  }

  onDeleteSelectedProducts(): void {
    const selected = this.selectedProducts();
    if (selected.length === 0) {
      return;
    }

    // Delete selected products
    const currentProducts = this.productTableData();
    
    // Use id for comparison if available, otherwise use skuCode as fallback
    const selectedIds = new Set(
      selected.map(p => p.id || p.skuCode)
    );
    
    const updatedProducts = currentProducts.filter(product => {
      const productId = product.id || product.skuCode;
      return !selectedIds.has(productId);
    });
    
    this.productTableData.set(updatedProducts);
    this.selectedProducts.set([]);
    
    // Clear validation errors
    this.clearValidationError('products');
  }

  onDeleteProduct(product: LogicTransferDetail): void {
    const currentProducts = this.productTableData();
    const updatedProducts = currentProducts.filter(
      p => p.skuCode !== product.skuCode
    );
    this.productTableData.set(updatedProducts);
  }

  // Get validation errors - returns array of error messages (like purchase return order edit)
  private getValidationErrors(): string[] {
    const errors: string[] = [];

    // Validate form fields - use both form.value and formData signal
    const formValue = this.form.value;
    const formDataValue = this.formData();
    
    // In edit mode, also check initialData for readonly fields (code, transferType, supportWarehouseCode)
    // These fields are disabled but still need to have values
    let code = formValue.code || formDataValue?.code;
    let transferType = formValue.transferType || formDataValue?.transferType;
    let supportWarehouseCode = formValue.supportWarehouseCode || formDataValue?.supportWarehouseCode;
    
    if (this.editMode && this.initialData) {
      // For readonly fields in edit mode, use initialData as fallback
      if (!code) {
        code = this.initialData.code;
      }
      if (!transferType) {
        transferType = this.initialData.transferType;
      }
      if (!supportWarehouseCode) {
        // Try multiple sources for source warehouse
        const mappedCodes = this.mappedWarehouseCodes();
        supportWarehouseCode = mappedCodes.supportCode 
          || this.initialData.supportWarehouseCode
          || (this.initialData.supportLogicWarehouseId ? this.mapWarehouseIdToCode(this.initialData.supportLogicWarehouseId) : null);
      }
    }
    
    // In edit mode, also check initialData for receiveWarehouseCode and expectedReceiveDate first
    let receiveWarehouseCode = formValue.receiveWarehouseCode || formDataValue?.receiveWarehouseCode || '';
    let expectedReceiveDate = formValue.expectedReceiveDate || formDataValue?.expectedReceiveDate;
    
    if (this.editMode && this.initialData) {
      // For receiveWarehouseCode, check multiple sources
      if (!receiveWarehouseCode) {
        const mappedCodes = this.mappedWarehouseCodes();
        receiveWarehouseCode = mappedCodes.receiveCode 
          || this.initialData.receiveWarehouseCode
          || (this.initialData.receivingLogicWarehouseId ? this.mapWarehouseIdToCode(this.initialData.receivingLogicWarehouseId) : null)
          || '';
      }
      // For expectedReceiveDate, check initialData
      if (!expectedReceiveDate && this.initialData.expectedReceiveDate) {
        expectedReceiveDate = this.initialData.expectedReceiveDate;
      }
    }
    
    // Use formData signal if form.value is empty, otherwise use form.value
    const effectiveValue = {
      code: code || '',
      transferType: transferType || '',
      supportWarehouseCode: supportWarehouseCode || '',
      receiveWarehouseCode: receiveWarehouseCode || '',
      expectedReceiveDate: expectedReceiveDate || null,
    };
    
    if (!effectiveValue.code) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.transfer_order_number_required'));
    }
    if (!effectiveValue.transferType) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.transfer_type_required'));
    }
    if (!effectiveValue.supportWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.source_logic_warehouse_required'));
    }
    if (!effectiveValue.receiveWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.destination_logic_warehouse_required'));
    }
    if (effectiveValue.supportWarehouseCode && effectiveValue.receiveWarehouseCode && 
        effectiveValue.supportWarehouseCode === effectiveValue.receiveWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.warehouses_must_be_different'));
    }
    if (!effectiveValue.expectedReceiveDate) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.expected_receive_date_required'));
    }

    // Validate products
    const products = this.productTableData();
    if (products.length === 0) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.no_products'));
    } else {
      products.forEach((product, index) => {
        // Validate quantity
        if (!product.quantity || product.quantity <= 0) {
          const errorMsg = this.translateService.instant('logic_transfer.form.validation.quantity_required');
          // Only add if not already in array (avoid duplicates)
          if (!errors.includes(errorMsg)) {
            errors.push(errorMsg);
          }
        } else if (product.stockNum !== undefined && product.stockNum !== null && product.quantity > product.stockNum) {
          // For quantity exceeds stock, include product name to make it unique
          const errorMsg = this.translateService.instant('logic_transfer.form.validation.quantity_exceeds_stock');
          // Only add if not already in array (avoid duplicates)
          if (!errors.includes(errorMsg)) {
            errors.push(errorMsg);
          }
        }
      });
    }

    // Remove duplicates before returning (using Set to preserve order)
    const uniqueErrors = Array.from(new Set(errors));
    return uniqueErrors;
  }

  private validateBeforeSave(): boolean {
    const errors: string[] = [];
    // Start with existing field errors to preserve validation from onCellValueChange
    const existingFieldErrors = { ...this.fieldValidationErrors() };
    const fieldErrors: Record<string, string> = { ...existingFieldErrors };

    // Validate form fields - use both form.value and formData signal
    const formValue = this.form.value;
    const formDataValue = this.formData();
    
    // In edit mode, also check initialData for readonly fields (code, transferType, supportWarehouseCode)
    // These fields are disabled but still need to have values
    let code = formValue.code || formDataValue?.code;
    let transferType = formValue.transferType || formDataValue?.transferType;
    let supportWarehouseCode = formValue.supportWarehouseCode || formDataValue?.supportWarehouseCode;
    
    if (this.editMode && this.initialData) {
      // For readonly fields in edit mode, use initialData as fallback
      if (!code) {
        code = this.initialData.code;
      }
      if (!transferType) {
        transferType = this.initialData.transferType;
      }
      if (!supportWarehouseCode) {
        // Try multiple sources for source warehouse
        const mappedCodes = this.mappedWarehouseCodes();
        supportWarehouseCode = mappedCodes.supportCode 
          || this.initialData.supportWarehouseCode
          || (this.initialData.supportLogicWarehouseId ? this.mapWarehouseIdToCode(this.initialData.supportLogicWarehouseId) : null);
      }
    }
    
    // In edit mode, also check initialData for receiveWarehouseCode and expectedReceiveDate first
    let receiveWarehouseCode = formValue.receiveWarehouseCode || formDataValue?.receiveWarehouseCode || '';
    let expectedReceiveDate = formValue.expectedReceiveDate || formDataValue?.expectedReceiveDate;
    
    if (this.editMode && this.initialData) {
      // For receiveWarehouseCode, check multiple sources
      if (!receiveWarehouseCode) {
        const mappedCodes = this.mappedWarehouseCodes();
        receiveWarehouseCode = mappedCodes.receiveCode 
          || this.initialData.receiveWarehouseCode
          || (this.initialData.receivingLogicWarehouseId ? this.mapWarehouseIdToCode(this.initialData.receivingLogicWarehouseId) : null)
          || '';
      }
      // For expectedReceiveDate, check initialData
      if (!expectedReceiveDate && this.initialData.expectedReceiveDate) {
        expectedReceiveDate = this.initialData.expectedReceiveDate;
      }
    }
    
    // Use formData signal if form.value is empty, otherwise use form.value
    const effectiveValue = {
      code: code || '',
      transferType: transferType || '',
      supportWarehouseCode: supportWarehouseCode || '',
      receiveWarehouseCode: receiveWarehouseCode || '',
      expectedReceiveDate: expectedReceiveDate || null,
    };
    
    if (!effectiveValue.code) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.transfer_order_number_required'));
    }
    if (!effectiveValue.transferType) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.transfer_type_required'));
    }
    if (!effectiveValue.supportWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.source_logic_warehouse_required'));
    }
    if (!effectiveValue.receiveWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.destination_logic_warehouse_required'));
    }
    if (effectiveValue.supportWarehouseCode && effectiveValue.receiveWarehouseCode && 
        effectiveValue.supportWarehouseCode === effectiveValue.receiveWarehouseCode) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.warehouses_must_be_different'));
    }
    if (!effectiveValue.expectedReceiveDate) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.expected_receive_date_required'));
    }

    // Validate products
    const products = this.productTableData();
    if (products.length === 0) {
      errors.push(this.translateService.instant('logic_transfer.form.validation.no_products'));
    } else {
      products.forEach((product, index) => {
        // Always validate - clear previous error first
        delete fieldErrors[`${index}_quantity`];
        
        // Validate quantity
        if (!product.quantity || product.quantity <= 0) {
          fieldErrors[`${index}_quantity`] = this.translateService.instant('logic_transfer.form.validation.quantity_required');
        } else if (product.stockNum !== undefined && product.stockNum !== null && product.quantity > product.stockNum) {
          fieldErrors[`${index}_quantity`] = this.translateService.instant('logic_transfer.form.validation.quantity_exceeds_stock');
        }
        // If validation passes, fieldErrors[`${index}_quantity`] will be undefined (already deleted)
      });
    }

    this.validationErrors.set(errors);
    this.fieldValidationErrors.set(fieldErrors);

    return errors.length === 0 && Object.keys(fieldErrors).length === 0;
  }

  private clearValidationError(type: 'warehouse' | 'products'): void {
    if (type === 'warehouse') {
      const errors = this.validationErrors().filter(error => 
        !error.includes('warehouse') && !error.includes('different')
      );
      this.validationErrors.set(errors);
    } else if (type === 'products') {
      this.fieldValidationErrors.set({});
    }
  }

  private openProductSelectionDialog(): void {
    // Read warehouse code from multiple sources to ensure we get the value
    // Priority: form value > signal > mapped codes > initialData > map from ID
    let warehouseCode = this.form.get('supportWarehouseCode')?.value 
      || this.sourceWarehouseCodeSignal();
    
    // If still not found, check mapped codes from initial data
    if (!warehouseCode) {
      const mappedCodes = this.mappedWarehouseCodes();
      warehouseCode = mappedCodes.supportCode;
    }
    
    // If still not found, check initialData directly
    if (!warehouseCode && this.initialData) {
      warehouseCode = this.initialData.supportWarehouseCode;
      
      // If still not found, try to map from warehouse ID
      if (!warehouseCode) {
        if (this.initialData.supportLogicWarehouseId) {
          warehouseCode = this.mapWarehouseIdToCode(this.initialData.supportLogicWarehouseId);
        } else if (this.initialData.supportWarehouseId) {
          warehouseCode = this.mapWarehouseIdToCode(this.initialData.supportWarehouseId);
        }
      }
    }
    
    if (!warehouseCode) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant('logic_transfer.form.errors.select_source_warehouse_first'),
      });
      return;
    }
    
    // Set warehouse code to signal before opening dialog
    // This will trigger the stock API call in the dialog
    this.dialogWarehouseCode.set(warehouseCode);
    this.showItemDialog.set(true);
  }

  onItemDialogVisibleChange(visible: boolean): void {
    this.showItemDialog.set(visible);
  }


  // Generate unique ID for product items
  private generateProductId(): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `product-${timestamp}-${random}`;
  }

  // Handle stock selection dialog confirm
  // Stock items already have stockNum and reservedNum, so we can directly map them
  onStockDialogConfirm(selectedItems: StockSelectionItem[]): void {
    if (selectedItems.length === 0) {
      return;
    }

    // Map StockSelectionItem[] to LogicTransferDetail[]
    const transferDetails: LogicTransferDetail[] = selectedItems.map((item) => {
      // Quantity defaults to 1 (user can edit in table)
      // stockNum should be availableQuantity (stockNum - reservedNum) from dialog
      return {
        id: this.generateProductId(),
        spuCode: item.spuCode,
        spuName: item.spuName,
        skuCode: item.skuCode,
        skuName: item.skuName,
        quantity: 1, // Default quantity is 1 (user can edit in table)
        stockNum: item.availableQuantity, // Use available quantity (stockNum - reservedNum)
      } as LogicTransferDetail;
    });

    // Add new items to the table (avoid duplicates)
    const currentData = this.productTableData();
    const newItems: LogicTransferDetail[] = [];

    transferDetails.forEach((transferDetail) => {
      const existingItem = currentData.find(
        (existing) => existing.skuCode === transferDetail.skuCode
      );
      if (!existingItem) {
        newItems.push(transferDetail);
      }
    });

    if (newItems.length > 0) {
      const newList = [...currentData, ...newItems];
      this.productTableData.set(newList);
      this.clearValidationError('products');
    }
  }


  private saveLogicTransfer(formValueFromEvent?: any): void {
    // Prevent multiple simultaneous save attempts (e.g., double-click)
    if (this.loading()) {
      return;
    }

    // Clear any previous validation errors from signals to avoid duplicates
    // (validateBeforeSave() may have been called earlier and set errors in signals)
    this.validationErrors.set([]);
    this.fieldValidationErrors.set({});

    // Validate before saving - get validation errors directly (like purchase return order edit)
    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      // Show validation errors (each error only once)
      // Don't use key - let preventDuplicates in p-toast handle duplicates
      validationErrors.forEach(error => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: error,
        });
      });
      return;
    }

    // Prefer latest form value from event; fallback to internal form snapshot
    const formValue = formValueFromEvent || this.form.value;

    // Quick guard: require both warehouses (this check is redundant but kept as safety)
    // Note: This should already be caught by validateBeforeSave above, so we just return without showing error
    if (!formValue.supportWarehouseCode || !formValue.receiveWarehouseCode) {
      // Don't show error here - it's already shown by validateBeforeSave
      return;
    }

    // Resolve warehouse by code
    const supportWh = this.findWarehouseByCode(formValue.supportWarehouseCode);
    const receiveWh = this.findWarehouseByCode(formValue.receiveWarehouseCode);

    if (!supportWh || !supportWh.id || !receiveWh || !receiveWh.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Warehouse not found. Please re-select warehouses.',
      });
      return;
    }

    this.loading.set(true);
    this.pendingSaveFormValue = formValue;
    this.evaluateFefoOverrides(supportWh.id, () => this.submitLogicTransfer(formValue));
  }

  private evaluateFefoOverrides(warehouseId: number, onReady: () => void): void {
    const candidates = this.productTableData().filter((product) => !!product.batchId?.trim());
    if (candidates.length === 0) {
      onReady();
      return;
    }

    forkJoin(
      candidates.map((product) =>
        this.batchManagementService
          .resolveSale({
            skuCode: product.skuCode,
            warehouseId,
            quantity: product.quantity > 0 ? product.quantity : 1,
            suggestOnly: true,
          })
          .pipe(
            switchMap((response: unknown) =>
              BlobConverterUtil.isBlob(response)
                ? from(BlobConverterUtil.convertToJson(response))
                : of(response),
            ),
            catchError(() => of(null)),
            map((result) => ({ skuCode: product.skuCode, result })),
          ),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          const bySku = new Map(rows.map((row) => [row.skuCode, row.result]));
          const updated = this.productTableData().map((product) => {
            const result = bySku.get(product.skuCode);
            if (!result) {
              return product;
            }
            return {
              ...product,
              lotRequired: isLotRequiredFromResolve(result),
              fefoSuggestedBatchId: fefoSuggestedBatchId(result),
            };
          });
          this.productTableData.set(updated);

          this.pendingFefoLines = updated.filter((product) =>
            needsFefoOverrideReason({
              lotRequired: product.lotRequired,
              chosenLot: product.batchId,
              suggestedLot: product.fefoSuggestedBatchId,
              overrideReason: product.overrideReason,
            }),
          );

          if (this.pendingFefoLines.length > 0) {
            this.loading.set(false);
            this.openNextFefoOverrideDialog();
            return;
          }
          onReady();
        },
        error: () => {
          this.loading.set(false);
          this.pendingSaveFormValue = null;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to validate FEFO lot selection',
          });
        },
      });
  }

  private submitLogicTransfer(formValue: any): void {
    const formatDate = (date: any): string | null => {
      const result = formatDateToISOWithoutTz(date, false);
      return result || null;
    };

    const supportWh = this.findWarehouseByCode(formValue.supportWarehouseCode);
    const receiveWh = this.findWarehouseByCode(formValue.receiveWarehouseCode);

    if (!supportWh || !supportWh.id || !receiveWh || !receiveWh.id) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Warehouse not found. Please re-select warehouses.',
      });
      return;
    }

    const transferData = {
      ...(this.initialData?.id && { id: this.initialData.id }),
      code: formValue.code,
      transferType: formValue.transferType,
      description: formValue.description || '',
      supportLogicWarehouseId: supportWh.id,
      receivingLogicWarehouseId: receiveWh.id,
      plannedDeliveryDate: formatDate(formValue.expectedReceiveDate),
      items: this.productTableData().map((product) => ({
        id: product.id
          ? typeof product.id === 'string'
            ? parseInt(product.id, 10)
            : product.id
          : undefined,
        skuCode: product.skuCode,
        plannedQty: product.quantity || 0,
        batchId: product.batchId || undefined,
        overrideReason: product.overrideReason || undefined,
      })),
      supportWarehouseId: formValue.supportWarehouseCode,
      supportWarehouseCode: formValue.supportWarehouseCode,
      supportWarehouseName:
        supportWh.name || this.getWarehouseName(formValue.supportWarehouseCode),
      receiveWarehouseId: formValue.receiveWarehouseCode,
      receiveWarehouseCode: formValue.receiveWarehouseCode,
      receiveWarehouseName:
        receiveWh.name || this.getWarehouseName(formValue.receiveWarehouseCode),
      expectedReceiveDate: formatDate(formValue.expectedReceiveDate),
      serialNumber: this.initialData?.serialNumber || 0,
      allotNum: this.getTotalTransferQuantity(),
      status: this.initialData?.status || 'PENDING',
      createdName: this.initialData?.createdName || 'Current User',
      creationDate:
        this.initialData?.creationDate ||
        new Date().toISOString().replace('T', ' ').substring(0, 19),
    };

    this.logicTransferService.saveLogicTransfer(transferData).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message || 'Logic transfer saved successfully',
        });
        this.skipCanDeactivate = true;
        this.addFormComponent?.form?.markAsPristine();
        this.form?.markAsPristine();
        this.router.navigate([this.config.listRoute]);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save logic transfer',
        });
      },
    });
  }

  private cancel(): void {
    this.router.navigate([this.config.listRoute]);
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return Boolean(
      !(this.addFormComponent?.form?.dirty || this.form?.dirty)
    );
  }

  private getWarehouseName(warehouseCode: string): string {
    const allWarehouses = this.allWarehouses();
    const warehouse = allWarehouses.find(w => w.code === warehouseCode);
    if (warehouse && warehouse.name) {
      return warehouse.name;
    }
    return warehouseCode;
  }

  private findWarehouseByCode(code: string) {
    if (!code) return undefined;
    return this.allWarehouses().find((w) => w.code === code);
  }

  private getTotalTransferQuantity(): number {
    return this.productTableData().reduce((total, product) => total + (product.quantity || 0), 0);
  }
}
